/* @flow */


import React from 'react';

import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Grid from '@material-ui/core/Grid';

import ButtonComponent from 'components/buttons/button';
import FormErrorsComponent from 'components/formErrors';
import IntlMessageComponent from 'components/formatters/intlMessage';
import MandatoryFormFieldsComponent from 'components/form/mandatory';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';
import AmountFormatterComponent from 'components/formatters/amount';
import CompanyAddressComponent from 'components/company/address';
import DateFormatterComponent from 'components/formatters/date';
import UploadFieldComponent from 'components/formFields/upload';

import SellInvoiceTabUploadXmlForm from './model';
import InvoiceDraft from 'models/invoices/seller/invoiceDraft';

type SellInvoiceTabUploadXmlViewComponentProps = {
  form: SellInvoiceTabUploadXmlForm;
  formErrors: string[];
  isProcessing: boolean;
  showUploadButton: boolean;
  invoiceDraft: InvoiceDraft;
  filename: string;
  showChangeFlow: boolean;
  handleBooleanChange: () => void;
  handleDocumentSelect: () => void;
  handleDocumentRemove: () => void;
  checkFile: () => void;
  cancelFile: () => void;
  uploadFile: () => void;
  changeToPdfFlow: () => void;
}

export default function SellInvoiceTabUploadXmlViewComponent(props: SellInvoiceTabUploadXmlViewComponentProps) {
  const defaultFieldOptions = {
    margin: 'normal', fullWidth: true
  };

  return (
    <React.Fragment>
      <FormErrorsComponent formErrors={props.formErrors} />

      <PageSubTitleViewComponent id="page.sellInvoice.TabUploadXml.title" />

      <Box mt={1}></Box>
      {!props.invoiceDraft &&
        <Box mt={3}>
          <Grid container spacing={6} alignItems="center" >
            <Grid item xs={12} md={6}>
              <Box mt={3}>
                <UploadFieldComponent
                  disabled={props.isProcessing}
                  displayProps={defaultFieldOptions}
                  filename={props.filename}
                  isRequired={true}
                  label={`sellinvoice.xml.upload`}
                  successHandler={props.handleDocumentSelect}
                  removeHandler={props.handleDocumentRemove}
                  acceptFormats={"text/xml"}
                />
              </Box>
            </Grid>

            {props.showChangeFlow &&
              <Grid item xs={12} md={6}>

                <Box p={5} bgcolor="#eaeaea" >
                  <Box >
                    <IntlMessageComponent id="page.sellInvoice.tabuploadxml.changetopdfexplanation" /><br /><br />
                    <Box display="flex" justifyContent="center">
                      <ButtonComponent
                        color="primary"
                        variant="contained"
                        isProcessing={props.isProcessing}
                        onClick={props.changeToPdfFlow}
                      ><IntlMessageComponent id="page.sellInvoice.TabUploadXml.button.pdfflow" /></ButtonComponent>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            }
          </Grid>
        </Box>
      }

      {props.invoiceDraft &&
        <React.Fragment>
          <Box mt={3}>
            <IntlMessageComponent id="page.sellInvoice.tabuploadxml.verifyinvoice" />
          </Box>
          <Box mt={2} py={3} bgcolor="lightblue.main">
            <Grid container direction="row" justify="space-between" alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Box px={3}>
                  <Box display="flex">
                    <Box flexGrow={1} mb={1}>
                      <IntlMessageComponent id="page.sellInvoice.tab6.summary.debtor.name" />
                    </Box>
                    <Box>
                      <CompanyAddressComponent company={props.invoiceDraft.debtorCompany} />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box px={3}>

                  <Box display="flex">
                    <Box flexGrow={1} mb={1}>
                      <IntlMessageComponent id="page.sellInvoice.tab2.issueDate.label" />
                    </Box>
                    <Box>
                      <DateFormatterComponent date={props.invoiceDraft.issueDate} format="DD MMMM YYYY" />
                    </Box>
                  </Box>
                  <Box display="flex">
                    <Box flexGrow={1} mb={1}>
                      <IntlMessageComponent id="page.sellInvoice.tab6.summary.duedate" />
                    </Box>
                    <Box>
                      <DateFormatterComponent date={props.invoiceDraft.dueDate} format="DD MMMM YYYY" />
                    </Box>
                  </Box>
                  <Box display="flex">
                    <Box flexGrow={1} mb={1}>
                      <IntlMessageComponent id="page.sellInvoice.tab6.summary.debtamount" />
                    </Box>
                    <Box>
                      <AmountFormatterComponent amount={props.invoiceDraft.debtAmount} />
                    </Box>
                  </Box>

                  <Box display="flex">
                    <Box flexGrow={1} mb={1}>
                      <IntlMessageComponent id="page.sellInvoice.tab2.reference.label" />
                    </Box>
                    <Box>
                      {props.invoiceDraft.reference}
                    </Box>
                  </Box>

                </Box>
              </Grid>
            </Grid>
          </Box>
        </React.Fragment>
      }

      {!props.showUploadButton &&
        <Box mt={5}>
          <FormControl component="fieldset" fullWidth required>
            <FormLabel component="legend">
              <IntlMessageComponent id="page.sellInvoice.tab1.serviceAndGoodsDelivered?" />
            </FormLabel>

            <RadioGroup
              aria-label="goods delivered"
              name="deliveredGoods"
              onChange={props.handleBooleanChange('deliveredGoods')}
              row
              value={props.form.deliveredGoods.toString()}
            >
              <FormControlLabel
                control={<Radio color="primary" />}
                label={<IntlMessageComponent id="general.label.yes" />}
                value="true"
              />
              <FormControlLabel
                control={<Radio color="primary" />}
                label={<IntlMessageComponent id="general.label.no" />}
                value="false"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      }

      <Box mt={5}>
        {!props.showUploadButton &&
          <React.Fragment>
            <ButtonComponent
              color="primary"
              variant="contained"
              disabled={props.form.file === null || !props.form.deliveredGoods || props.formErrors.length > 0}
              isProcessing={props.isProcessing}
              onClick={props.checkFile}
            ><IntlMessageComponent id="page.sellInvoice.TabUploadXml.button.validate" /></ButtonComponent>
            <MandatoryFormFieldsComponent />
          </React.Fragment>}
        {props.showUploadButton &&
          <Box mt={3} ml={3}>
            <Grid container spacing={6} alignItems="center" >
              <ButtonComponent
                color="gray"
                variant="contained"
                disabled={props.form.file === null || props.formErrors.length > 0}
                isProcessing={props.isProcessing}
                onClick={props.cancelFile}
              ><IntlMessageComponent id="page.sellInvoice.TabUploadXml.button.cancel" /></ButtonComponent>
              <Box ml={3}>
                <ButtonComponent
                  color="primary"
                  variant="contained"
                  disabled={props.form.file === null || props.formErrors.length > 0}
                  isProcessing={props.isProcessing}
                  onClick={props.uploadFile}
                ><IntlMessageComponent id="page.sellInvoice.TabUploadXml.button.confirmupload" /></ButtonComponent>
              </Box>
            </Grid>
          </Box>}
      </Box>
    </React.Fragment>
  );
}
